import { Organization, OrgFeatureFlags, OrgParentPolicies, OrgStaffPolicies } from '../types';
import { GoogleGenAI } from '@google/genai';

export interface ModuleDefinition {
  key: keyof OrgFeatureFlags;
  label: string;
  shortLabel: string;
  icon: string;
  description: string;
  category: 'core' | 'communication' | 'academics' | 'wellbeing' | 'ai';
  aiPowered?: boolean;
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  // Core Essentials
  {
    key: 'homework',
    label: 'Homework & Assignments',
    shortLabel: 'Homework',
    icon: 'assignment',
    description: 'Digital homework board, task checklists, photo whiteboard attachments, and completion tracking.',
    category: 'core'
  },
  {
    key: 'attendance',
    label: 'Daily Attendance & Calendar',
    shortLabel: 'Attendance',
    icon: 'event_available',
    description: 'Smart attendance register (Present/Late/Absent), visual monthly calendar, and absence records.',
    category: 'core'
  },
  {
    key: 'announcements',
    label: 'Broadcast Announcements',
    shortLabel: 'Broadcasts',
    icon: 'campaign',
    description: 'Institution-wide and class-specific notices, alerts, and photo broadcasts.',
    category: 'communication'
  },

  // Communication
  {
    key: 'chat',
    label: 'Parent-Teacher Multilingual Chat',
    shortLabel: 'Direct Chat',
    icon: 'chat',
    description: 'Real-time 1-on-1 messaging with instant bidirectional auto-translation across 20+ languages.',
    category: 'communication',
    aiPowered: true
  },
  {
    key: 'calendar',
    label: 'School & Academic Events',
    shortLabel: 'Calendar',
    icon: 'calendar_month',
    description: 'Academic events schedule, holidays, exam dates, deadlines, and parent calendars.',
    category: 'communication'
  },

  // Academics & AI
  {
    key: 'assessments',
    label: 'Assessments & Gradebook',
    shortLabel: 'Assessments',
    icon: 'fact_check',
    description: 'Exam scheduling, grading, scorecards, percentage calculations, and parent grade reports.',
    category: 'academics'
  },
  {
    key: 'aiTutor',
    label: 'AI Homework Tutor ("I\'m Stuck?")',
    shortLabel: 'AI Tutor',
    icon: 'psychology',
    description: 'Interactive Socratic AI tutor that gives hints, concept explanations, and learning steps without giving away answers.',
    category: 'ai',
    aiPowered: true
  },
  {
    key: 'insights',
    label: 'AI Teacher Insights & Weekly Pulse',
    shortLabel: 'AI Insights',
    icon: 'auto_awesome',
    description: 'AI-synthesized weekly classroom insights correlating homework friction, attendance, and student feedback.',
    category: 'ai',
    aiPowered: true
  },

  // Wellbeing & Culture
  {
    key: 'kudos',
    label: 'Kudos & Positive Recognition',
    shortLabel: 'Kudos',
    icon: 'military_tech',
    description: 'Gamified recognition badges (Teacher-Student, Parent-Teacher, Principal Awards) with celebration cards.',
    category: 'wellbeing'
  },
  {
    key: 'weeklyCheckin',
    label: 'Weekly Wellbeing Check-in',
    shortLabel: 'Check-in',
    icon: 'favorite',
    description: 'Parent-submitted weekly surveys on sleep, stress levels, comprehension, and routine.',
    category: 'wellbeing'
  },
  {
    key: 'familyConnect',
    label: 'Family Connect & Character Compass',
    shortLabel: 'Family Connect',
    icon: 'explore',
    description: 'Daily character-building conversation prompts for parents to engage children at home.',
    category: 'wellbeing'
  }
];

export interface StaffPolicyDefinition {
  key: keyof OrgStaffPolicies;
  associatedFeature: keyof OrgFeatureFlags;
  label: string;
  description: string;
  defaultValue: boolean;
  icon: string;
  aiPowered?: boolean;
}

export const STAFF_POLICY_DEFINITIONS: StaffPolicyDefinition[] = [
  {
    key: 'enableTeacherInsights',
    associatedFeature: 'insights',
    label: 'AI Classroom Insights & Weekly Pulse',
    description: 'Allows teachers to generate AI-synthesized weekly classroom insights correlating homework friction, attendance, and student feedback.',
    icon: 'auto_awesome',
    aiPowered: true,
    defaultValue: true
  },
  {
    key: 'enableTeacherAssessments',
    associatedFeature: 'assessments',
    label: 'Assessments, Exams & Gradebook',
    description: 'Allows teachers to schedule exams/quizzes, record student marks, edit scorecards, and submit results for approval.',
    icon: 'fact_check',
    defaultValue: true
  },
  {
    key: 'enableTeacherKudos',
    associatedFeature: 'kudos',
    label: 'Positive Recognition & Student Kudos',
    description: 'Allows teachers to award positive character badges, stars, and encouragement certificates to students.',
    icon: 'military_tech',
    defaultValue: true
  },
  {
    key: 'enableTeacherChat',
    associatedFeature: 'chat',
    label: 'Direct 1-on-1 Parent Messaging',
    description: 'Allows teachers to initiate direct chats and respond to parent queries with automatic multilingual translation.',
    icon: 'forum',
    defaultValue: true
  },
  {
    key: 'enableTeacherAttendanceCalendar',
    associatedFeature: 'attendance',
    label: 'Daily Attendance Registers & Stats',
    description: 'Allows teachers to mark daily class attendance registers (Present/Late/Absent) and view student stats.',
    icon: 'event_available',
    defaultValue: true
  },
  {
    key: 'enableTeacherHomework',
    associatedFeature: 'homework',
    label: 'Digital Homework & Whiteboard Postings',
    description: 'Allows teachers to post daily assignments, attach whiteboard photos, and track student completion checklists.',
    icon: 'assignment',
    defaultValue: true
  },
  {
    key: 'allowTeacherInviteCodeAudit',
    associatedFeature: 'homework', // Base feature always entitled
    label: 'Class Invite Code Audit & Redemption Ledger',
    description: 'Allows class teachers to view the redemption history and audit log of parent invitation codes used for their classes.',
    icon: 'pin',
    defaultValue: true
  }
];

export interface ParentPolicyDefinition {
  key: keyof OrgParentPolicies;
  associatedFeature: keyof OrgFeatureFlags;
  label: string;
  description: string;
  defaultValue: boolean;
  icon: string;
  aiPowered?: boolean;
}

export const PARENT_POLICY_DEFINITIONS: ParentPolicyDefinition[] = [
  {
    key: 'allowParentInitiatedChat',
    associatedFeature: 'chat',
    label: 'Allow Parents to Initiate New Chats',
    description: 'When disabled, parents can only reply to messages initiated by the teacher or principal.',
    icon: 'chat',
    defaultValue: true
  },
  {
    key: 'allowParentReportAbsence',
    associatedFeature: 'attendance',
    label: 'Allow Online Absence Reporting',
    description: 'Enable parents to submit advance absence notices and medical note attachments.',
    icon: 'event_busy',
    defaultValue: true
  },
  {
    key: 'enableAiTutorForStudents',
    associatedFeature: 'aiTutor',
    label: 'Allow AI Tutor Usage by Students & Parents',
    description: 'Controls whether the "I\'m Stuck?" Socratic AI tutoring helper appears on homework tasks.',
    icon: 'psychology',
    aiPowered: true,
    defaultValue: true
  },
  {
    key: 'enableAssessmentGradebook',
    associatedFeature: 'assessments',
    label: 'Publish Assessment Results to Parents',
    description: 'Controls whether test results, grades, and report cards are visible in the Parent app.',
    icon: 'grade',
    defaultValue: true
  },
  {
    key: 'showLeaderboardOrPublicKudos',
    associatedFeature: 'kudos',
    label: 'Show Public Classroom Kudos',
    description: 'When disabled, parents only see kudos awarded privately to their own child.',
    icon: 'military_tech',
    defaultValue: true
  },
  {
    key: 'enableWeeklyCheckin',
    associatedFeature: 'weeklyCheckin',
    label: 'Enable Weekly Wellbeing Check-in Form',
    description: 'Allows parents to submit weekly updates on their child’s academic and emotional wellbeing.',
    icon: 'favorite',
    defaultValue: true
  },
  {
    key: 'enableFamilyConnect',
    associatedFeature: 'familyConnect',
    label: 'Show Daily Family Connect Prompts',
    description: 'Displays the daily character-building conversation starters on the parent dashboard.',
    icon: 'explore',
    defaultValue: true
  }
];

export interface FeaturePreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  badge: string;
  featureFlags: OrgFeatureFlags;
  staffPolicies: OrgStaffPolicies;
  parentPolicies: OrgParentPolicies;
}

export const FEATURE_PRESETS: FeaturePreset[] = [
  {
    id: 'full_k12',
    name: 'Full K-12 School Suite',
    description: 'Complete all-in-one setup with AI tools, exams, attendance, kudos, multilingual chat, and family wellness.',
    icon: 'school',
    badge: 'Recommended',
    featureFlags: {
      homework: true,
      attendance: true,
      chat: true,
      announcements: true,
      calendar: true,
      assessments: true,
      kudos: true,
      aiTutor: true,
      weeklyCheckin: true,
      familyConnect: true,
      insights: true
    },
    staffPolicies: {
      enableTeacherInsights: true,
      enableTeacherAssessments: true,
      enableTeacherKudos: true,
      enableTeacherChat: true,
      enableTeacherAttendanceCalendar: true,
      enableTeacherHomework: true,
      enableTeacherAiHomeworkHelper: true
    },
    parentPolicies: {
      allowParentInitiatedChat: true,
      showLeaderboardOrPublicKudos: true,
      allowParentReportAbsence: true,
      enableAiTutorForStudents: true,
      enableWeeklyCheckin: true,
      enableFamilyConnect: true,
      enableAssessmentGradebook: true
    }
  },
  {
    id: 'lean_starter',
    name: 'Lean Starter (Core 3)',
    description: 'Essential lightweight launchpack: Homework, Attendance, and Announcements. Zero overwhelm for new schools.',
    icon: 'speed',
    badge: 'Lightweight',
    featureFlags: {
      homework: true,
      attendance: true,
      announcements: true,
      chat: false,
      calendar: false,
      assessments: false,
      kudos: false,
      aiTutor: false,
      weeklyCheckin: false,
      familyConnect: false,
      insights: false
    },
    staffPolicies: {
      enableTeacherInsights: false,
      enableTeacherAssessments: false,
      enableTeacherKudos: false,
      enableTeacherChat: false,
      enableTeacherAttendanceCalendar: true,
      enableTeacherHomework: true,
      enableTeacherAiHomeworkHelper: false
    },
    parentPolicies: {
      allowParentInitiatedChat: false,
      showLeaderboardOrPublicKudos: false,
      allowParentReportAbsence: true,
      enableAiTutorForStudents: false,
      enableWeeklyCheckin: false,
      enableFamilyConnect: false,
      enableAssessmentGradebook: false
    }
  },
  {
    id: 'coaching_academy',
    name: 'Coaching & Tuition Academy',
    description: 'Optimized for tutoring centers and coaching institutes: Focus on homework, tests, AI tutoring, and chat.',
    icon: 'menu_book',
    badge: 'Coaching',
    featureFlags: {
      homework: true,
      attendance: true,
      chat: true,
      announcements: true,
      calendar: true,
      assessments: true,
      kudos: true,
      aiTutor: true,
      weeklyCheckin: false,
      familyConnect: false,
      insights: true
    },
    staffPolicies: {
      enableTeacherInsights: true,
      enableTeacherAssessments: true,
      enableTeacherKudos: true,
      enableTeacherChat: true,
      enableTeacherAttendanceCalendar: true,
      enableTeacherHomework: true,
      enableTeacherAiHomeworkHelper: true
    },
    parentPolicies: {
      allowParentInitiatedChat: true,
      showLeaderboardOrPublicKudos: true,
      allowParentReportAbsence: true,
      enableAiTutorForStudents: true,
      enableWeeklyCheckin: false,
      enableFamilyConnect: false,
      enableAssessmentGradebook: true
    }
  },
  {
    id: 'academic_focused',
    name: 'Strict Academic & Testing',
    description: 'Focused strictly on classroom pedagogy: Homework, attendance, grading, and academic calendar without social features.',
    icon: 'quiz',
    badge: 'Academic',
    featureFlags: {
      homework: true,
      attendance: true,
      chat: false,
      announcements: true,
      calendar: true,
      assessments: true,
      kudos: false,
      aiTutor: true,
      weeklyCheckin: false,
      familyConnect: false,
      insights: true
    },
    staffPolicies: {
      enableTeacherInsights: true,
      enableTeacherAssessments: true,
      enableTeacherKudos: false,
      enableTeacherChat: false,
      enableTeacherAttendanceCalendar: true,
      enableTeacherHomework: true,
      enableTeacherAiHomeworkHelper: true
    },
    parentPolicies: {
      allowParentInitiatedChat: false,
      showLeaderboardOrPublicKudos: false,
      allowParentReportAbsence: true,
      enableAiTutorForStudents: true,
      enableWeeklyCheckin: false,
      enableFamilyConnect: false,
      enableAssessmentGradebook: true
    }
  }
];

/**
 * Checks if a specific module is enabled at the institution license/platform level (Tier 1 Super Admin).
 * Defaults to true if the organization or feature flags have not been configured yet (backwards compatible).
 */
export const isFeatureEnabled = (
  org: Organization | undefined | null,
  key: keyof OrgFeatureFlags
): boolean => {
  if (!org) return true;
  if (!org.featureFlags) return true;
  return org.featureFlags[key] !== false;
};

/**
 * Checks if a specific staff/teacher feature is enabled.
 * 1. Checks if the global module is enabled by Super Admin (featureKey).
 * 2. Checks if the School Principal has enabled it for Staff (staffPolicies).
 */
export const isStaffFeatureEnabled = (
  org: Organization | undefined | null,
  staffPolicyKey: keyof OrgStaffPolicies,
  featureKey?: keyof OrgFeatureFlags
): boolean => {
  if (!org) return true;
  
  // If global module is disabled by Super Admin, feature is disabled for staff
  if (featureKey && !isFeatureEnabled(org, featureKey)) {
    return false;
  }
  
  if (!org.staffPolicies) return true;
  return org.staffPolicies[staffPolicyKey] !== false;
};

/**
 * Checks if a specific parent policy is enabled.
 * 1. Checks if the underlying global feature is enabled at the organization level.
 * 2. Checks if the School Principal has enabled it for Parents (parentPolicies).
 */
export const isParentPolicyEnabled = (
  org: Organization | undefined | null,
  policyKey: keyof OrgParentPolicies | string,
  featureKey?: keyof OrgFeatureFlags
): boolean => {
  if (!org) return true;
  
  // If a parent policy depends on a broader feature flag, verify that first
  if (featureKey && !isFeatureEnabled(org, featureKey)) {
    return false;
  }
  
  if (!org.parentPolicies) return true;

  // Handle aliases gracefully
  const normalizedKey = (
    policyKey === 'allowParentAITutor' ? 'enableAiTutorForStudents' :
    policyKey === 'allowParentWeeklyCheckin' ? 'enableWeeklyCheckin' :
    policyKey === 'allowParentViewAssessments' ? 'enableAssessmentGradebook' :
    policyKey === 'allowParentTeacherChat' ? 'allowParentInitiatedChat' :
    policyKey === 'allowParentViewKudos' || policyKey === 'allowParentSendKudos' ? 'showLeaderboardOrPublicKudos' :
    policyKey === 'allowParentFamilyConnect' ? 'enableFamilyConnect' :
    policyKey
  ) as keyof OrgParentPolicies;

  const value = org.parentPolicies[normalizedKey];
  if (value !== undefined) return value !== false;
  
  const rawValue = (org.parentPolicies as any)[policyKey];
  if (rawValue !== undefined) return rawValue !== false;

  return true;
};

/**
 * Validates a Gemini API Key directly by issuing a lightweight test query.
 */
export const testGeminiApiKey = async (apiKey: string): Promise<{ success: boolean; message: string }> => {
  if (!apiKey || !apiKey.trim()) {
    return { success: false, message: 'Please enter an API key.' };
  }
  
  try {
    const aiClient = new GoogleGenAI({ apiKey: apiKey.trim() });
    const response = await aiClient.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: 'Ping: Reply with "OK" in 1 word.'
    });
    
    if (response && response.text) {
      return { success: true, message: 'API Key is verified and working!' };
    }
    return { success: false, message: 'API test returned empty response.' };
  } catch (error: any) {
    const errMsg = error.message || 'API key verification failed';
    return { success: false, message: errMsg };
  }
};

